import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  DsButtonV2Component,
  DsCardComponent,
  DsInputTextComponent,
} from '@customdesignsystem';
import { StepperLateralComponent } from '../../components/stepper-lateral/stepper-lateral.component';
import { STEP_THREE_LIST } from '../../constants/employer.constant';
import { SUBSCRIPTION_URLS } from '../../constants/subscription-routes.constant';
import {
  isValidMail,
  toEmployerOption,
  toEmployerSelectionFromOption,
} from '../../mappers/employer.mapper';
import { IEmployerRawFromService } from '../../models/subscription-api.model';
import { IEmployerOption } from '../../models/subscription.model';
import { SubscriptionService } from '../../services/subscription.service';
import { SubscriptionStore } from '../../services/subscription.store';

const CURRENT_STEP_INDEX = 2;

@Component({
  selector: 'app-employer-page',
  templateUrl: './employer.page.html',
  styleUrl: './employer.page.scss',
  imports: [
    CommonModule,
    StepperLateralComponent,
    DsButtonV2Component,
    DsCardComponent,
    DsInputTextComponent,
  ],
})
export class EmployerPage implements OnInit {
  readonly stepThree = STEP_THREE_LIST;
  readonly currentStepIndex = CURRENT_STEP_INDEX;

  employers: IEmployerOption[] = [];
  areEmployersReady: boolean = false;
  isEditStepper: boolean = false;
  selectedId: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly subscriptionSvc: SubscriptionService,
    private readonly store: SubscriptionStore
  ) {}

  ngOnInit(): void {
    this.isEditStepper = this.store.editMode();
    this.getAndSetEmployers();
  }

  get selectedEmployer(): IEmployerOption | undefined {
    return this.employers.find((employer) => employer.id === this.selectedId);
  }

  get canContinue(): boolean {
    return !!this.selectedEmployer?.emailSaved;
  }

  isSelected(employer: IEmployerOption): boolean {
    return employer.id === this.selectedId;
  }

  select(employer: IEmployerOption): void {
    this.selectedId = employer.id;
  }

  isEmailInvalid(employer: IEmployerOption): boolean {
    return employer.emailTouched && !isValidMail(employer.email);
  }

  canToggleEmail(employer: IEmployerOption): boolean {
    return employer.emailSaved || isValidMail(employer.email);
  }

  onEmailInput(employer: IEmployerOption, value: string): void {
    employer.email = value;
    employer.emailTouched = false;
  }

  onEmailBlur(employer: IEmployerOption): void {
    employer.emailTouched = true;
  }

  toggleEmail(employer: IEmployerOption): void {
    if (employer.emailSaved) {
      employer.emailSaved = false;
      return;
    }

    if (!isValidMail(employer.email)) {
      employer.emailTouched = true;
      return;
    }

    employer.emailSaved = true;
    employer.emailTouched = false;
  }

  goToNextStep(): void {
    if (!this.canContinue) return;

    this.saveSelectedEmployer();
    this.router.navigate([SUBSCRIPTION_URLS.confirm]);
  }

  goBack(): void {
    if (this.isEditStepper) {
      this.backToConfirmation();
      return;
    }

    this.goToPreviousStep();
  }

  saveChanges(): void {
    if (!this.canContinue) return;

    this.saveSelectedEmployer();
    this.backToConfirmation();
  }

  private saveSelectedEmployer(): void {
    const employer = this.selectedEmployer;

    if (employer) {
      this.store.setEmployer(toEmployerSelectionFromOption(employer));
    }
  }

  private backToConfirmation(): void {
    this.store.setEditMode(false);
    this.router.navigate([SUBSCRIPTION_URLS.confirm]);
  }

  goToPreviousStep(): void {
    this.router.navigate([SUBSCRIPTION_URLS.regimeFund]);
  }

  private async getAndSetEmployers(): Promise<void> {
    let loadedEmployers: IEmployerRawFromService[];

    try {
      loadedEmployers = await this.subscriptionSvc.loadEmployers();
    } catch (error) {
      console.error('Error fetching employers', error);
      this.router.navigate([SUBSCRIPTION_URLS.serviceError]);
      return;
    }

    if (loadedEmployers.length === 0) {
      this.store.setHasEmployer(false);
      this.router.navigate([SUBSCRIPTION_URLS.addEmployer]);
      return;
    }

    this.employers = loadedEmployers.map(toEmployerOption);
    this.restoreSavedSelection();
    this.areEmployersReady = true;
  }

  private restoreSavedSelection(): void {
    const saved = this.store.employer();
    const option = saved
      ? this.employers.find((employer) => employer.rut === saved.fullRut)
      : undefined;

    if (!saved || !option) return;

    option.email = saved.email;
    option.emailSaved = true;
    this.selectedId = option.id;
  }
}
